import { Loader2 } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  /** Tugmadagi matn amalning O'ZINI ataydi: "O'chirish", "Chiqish". */
  confirmLabel: string;
  destructive?: boolean;
  loading?: boolean;
  /**
   * Tasdiqlash tugmasi bosilmaydi.
   *
   * `loading` dan alohida: u "kutilmoqda", bu esa "kiritilgan qiymat
   * yaroqsiz" degani. Bitta bayroq bo'lganda oynada aylanayotgan
   * belgi paydo bo'lardi va hech narsa yuklanmasdi.
   */
  confirmDisabled?: boolean;
  onConfirm: () => void;
  children?: ReactNode;
}

/**
 * Tasdiqlash oynasi.
 *
 * Qaytarib bo'lmaydigan yoki ishni to'xtatadigan har bir amal shu
 * yerdan o'tadi. Sabab: bunday tugmalar odatda ro'yxat chekkasida,
 * zararsizlari yonida turadi va bir piksel xato butun yozuvni yo'q
 * qiladi.
 *
 * Tugmada "Ha" emas, amal nomi yoziladi: oynani o'qimay bosgan odam
 * ham nima bo'layotganini ko'radi.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  destructive = true,
  loading = false,
  confirmDisabled = false,
  onConfirm,
  children,
}: ConfirmDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        {children}

        <DialogFooter>
          <Button variant="ghost" disabled={loading} onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant={destructive ? "destructive" : "default"}
            disabled={loading || confirmDisabled}
            onClick={onConfirm}
          >
            {loading && <Loader2 className="size-4 animate-spin" />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
